import { useMemo, useState } from "react";
import { Phone, ArrowUpRight, Search, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Header, AnnouncementBar, Footer } from "@/components/Layout";
import { SITE } from "@/lib/site";
import { useSeo, breadcrumbSchema } from "@/lib/seo";

const FAQ_GROUPS = [
    {
        group: "Services & Scope",
        items: [
            {
                q: "What types of handyman services do you offer?",
                a: "We handle a wide range of work — drywall repair and texture, painting, fence repairs, door and window repairs, gutter cleaning, graffiti removal, porter services, and general maintenance for both homes and businesses. We also offer daily service calls and full turnover services.",
            },
            {
                q: "Do you work with both residential and commercial properties?",
                a: "Yes. We service homeowners, property managers, HOAs, offices, retail spaces, and other commercial buildings throughout Medford, Ashland, and the Rogue Valley.",
            },
            {
                q: "Can you handle small remodeling projects?",
                a: "Yes — minor remodels are right in our wheelhouse. Kitchen backsplash installs, bathroom upgrades, office-space improvements, and similar projects.",
            },
            {
                q: "Can you set up regular maintenance for my property?",
                a: "Yes. We offer scheduled maintenance plans for both residential and commercial properties — ideal for keeping everything running smoothly without surprises.",
            },
        ],
    },
    {
        group: "Booking & Scheduling",
        items: [
            {
                q: "How do I schedule a service appointment?",
                a: `Call us directly at ${SITE.phoneDisplay}, fill out our online service request form, or email ${SITE.emailDisplay} to book. We offer flexible scheduling to fit your timeline.`,
            },
            {
                q: "Do you offer emergency handyman services?",
                a: "Yes, in most cases. For urgent repairs — water leaks, broken fixtures, anything time-sensitive — give us a call and we'll do our best to get there quickly. 24-hour emergency calls available.",
            },
            {
                q: "What is your cancellation or rescheduling policy?",
                a: "We ask for at least 24 hours' notice for cancellations or schedule changes whenever possible.",
            },
            {
                q: "Do I need to be home or on-site during the service?",
                a: "No. As long as we have permission to enter and clear instructions, we'll complete the work and follow up with photos or a walkthrough afterward.",
            },
            {
                q: "How long does a typical service call take?",
                a: "Most jobs are completed within 1 to 3 hours, depending on complexity. We'll give you a time estimate when we provide your quote.",
            },
        ],
    },
    {
        group: "Pricing, Estimates & Materials",
        items: [
            {
                q: "Do you provide free estimates?",
                a: "Yes — we offer free on-site or virtual estimates for most jobs. We'll assess the work and give you a clear, written quote with no hidden fees.",
            },
            {
                q: "What are your rates for handyman services?",
                a: "Rates depend on the type and scope of the job. We charge by the hour for small tasks and provide flat-rate quotes for larger projects. Either way, you'll know what it costs before we start.",
            },
            {
                q: "What kind of materials or parts do you supply?",
                a: "We can supply standard materials or work with what you provide. For custom items or brand-specific parts, we'll usually recommend you purchase those directly.",
            },
        ],
    },
    {
        group: "Service Area & Credentials",
        items: [
            {
                q: "Are you licensed and insured?",
                a: "Absolutely. We're a fully licensed General Contractor (CCB #232477), bonded, and insured — peace of mind is built in.",
            },
            {
                q: "What areas around Medford do you serve?",
                a: "We serve Medford and the surrounding Rogue Valley — Ashland, Central Point, Phoenix, Talent, Eagle Point, White City, Jacksonville, and Grants Pass. We now also service Portland, Vancouver, WA, and Battle Ground, WA.",
            },
            {
                q: "Why should I choose 5 Star over other handymen in Medford?",
                a: "We specialize in all phases of construction — from start to finish — including handyman work, site development, and manufactured-home setup. With over 25 years of experience and deep local roots, we deliver efficient, high-quality workmanship. Our team is professional, punctual, and treats every property with care and respect.",
            },
        ],
    },
];

export default function Faq() {
    const [query, setQuery] = useState("");

    // FAQPage JSON-LD — this is the schema Google uses to show your Q&As
    // as expandable rich-snippet answers directly in search results.
    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ_GROUPS.flatMap((g) =>
            g.items.map((it) => ({
                "@type": "Question",
                name: it.q,
                acceptedAnswer: {
                    "@type": "Answer",
                    text: it.a,
                },
            })),
        ),
    };

    useSeo({
        title: "Handyman & General Contractor FAQ · Medford, Oregon | 5 Star Construction",
        description: "Answers to common questions about our handyman, general contractor, deck, fan, and manufactured-home services in Medford and the Rogue Valley — pricing, scheduling, service area, licensing (CCB #232477), and what to expect on the job.",
        path: "/faq",
        jsonLdKey: "faq",
        jsonLd: [
            faqJsonLd,
            breadcrumbSchema([
                { name: "Home", path: "/" },
                { name: "FAQ", path: "/faq" },
            ]),
        ],
    });

    const filtered = useMemo(() => {
        if (!query.trim()) return FAQ_GROUPS;
        const q = query.toLowerCase();
        return FAQ_GROUPS.map((g) => ({
            ...g,
            items: g.items.filter(
                (it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q),
            ),
        })).filter((g) => g.items.length > 0);
    }, [query]);

    const totalCount = FAQ_GROUPS.reduce((sum, g) => sum + g.items.length, 0);

    return (
        <div className="bg-[#F6F5F2] min-h-screen">
            <AnnouncementBar />
            <Header />

            {/* Hero */}
            <section className="relative bg-[#111111] text-[#F6F5F2] border-b-4 border-[#E11D2A] overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-24">
                    <div className="inline-flex items-center gap-2 mb-6 border border-[#E11D2A] px-3 py-1.5">
                        <HelpCircle className="w-4 h-4 text-[#E11D2A]" strokeWidth={2.5} />
                        <span className="font-['Manrope'] text-xs font-bold tracking-[0.22em] uppercase text-[#E11D2A]">FAQ · {totalCount} answers</span>
                    </div>
                    <h1 className="font-['Anton'] tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl uppercase max-w-4xl">
                        Got questions?{" "}
                        <span className="text-[#FFD400]">We&apos;ve got plain-English answers.</span>
                    </h1>
                    <p className="mt-7 font-['Manrope'] text-lg md:text-xl leading-relaxed text-[#F6F5F2]/80 max-w-3xl">
                        Everything we get asked most — pricing, scheduling, service area, what we&apos;ll need from you, and what to expect on the job. Don&apos;t see your question? Pick up the phone — we&apos;ll answer it.
                    </p>

                    {/* Search */}
                    <div className="mt-10 max-w-xl relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5F5F5F]" strokeWidth={2.25} />
                        <Input
                            data-testid="faq-search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search the FAQ — pricing, scheduling, areas..."
                            className="rounded-none h-14 pl-12 bg-white border-0 text-[#111812] focus:ring-2 focus:ring-[#E11D2A] font-['Manrope'] text-base"
                        />
                    </div>
                </div>
            </section>

            {/* FAQ Body */}
            <section className="bg-[#F6F5F2]">
                <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-16 md:py-24">
                    {filtered.length === 0 ? (
                        <div className="text-center py-20" data-testid="faq-no-results">
                            <div className="font-display text-2xl font-bold text-[#111812]">No matches for &quot;{query}&quot;</div>
                            <p className="font-['Manrope'] text-base text-[#5F5F5F] mt-2">Give us a call — we&apos;ll answer it directly.</p>
                            <a href={SITE.phoneHref} className="inline-block mt-6">
                                <Button className="rounded-none h-12 px-6 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide font-['Manrope']">
                                    <Phone className="w-4 h-4 mr-2" strokeWidth={2.5} />
                                    CALL {SITE.phoneDisplay}
                                </Button>
                            </a>
                        </div>
                    ) : (
                        filtered.map((g) => (
                            <div key={g.group} className="mb-14 last:mb-0" data-testid={`faq-group-${g.group.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
                                <div className="flex items-baseline gap-3 mb-6 pb-4 border-b-2 border-[#111111]">
                                    <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-[#111812]">{g.group}</h2>
                                    <span className="font-['Manrope'] text-xs font-bold uppercase tracking-[0.22em] text-[#5F5F5F]">{g.items.length} {g.items.length === 1 ? "question" : "questions"}</span>
                                </div>
                                <Accordion type="single" collapsible className="space-y-3">
                                    {g.items.map((it, idx) => (
                                        <AccordionItem
                                            key={it.q}
                                            value={`${g.group}-${idx}`}
                                            className="bg-white border border-[#E2DFD8] data-[state=open]:border-[#111111] data-[state=open]:shadow-block-sm transition-all"
                                            data-testid={`faq-item-${idx}`}
                                        >
                                            <AccordionTrigger className="px-5 md:px-7 py-5 text-left hover:no-underline group">
                                                <span className="flex items-center gap-4 flex-1">
                                                    <span className="font-['Anton'] text-2xl text-[#E11D2A] tracking-tight w-9 shrink-0">{String(idx + 1).padStart(2, "0")}</span>
                                                    <span className="font-display text-base md:text-lg font-bold tracking-tight text-[#111812] group-hover:text-[#E11D2A] transition-colors">
                                                        {it.q}
                                                    </span>
                                                </span>
                                            </AccordionTrigger>
                                            <AccordionContent className="px-5 md:px-7 pb-6 pt-0">
                                                <div className="ml-0 md:ml-[52px] pl-0 md:pl-4 md:border-l-2 md:border-[#E11D2A]">
                                                    <p className="font-['Manrope'] text-base text-[#111812] leading-relaxed">{it.a}</p>
                                                </div>
                                            </AccordionContent>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </div>
                        ))
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#FFD400] border-y-4 border-[#111111]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-14 md:py-16 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <h2 className="font-['Anton'] uppercase tracking-tight leading-[0.95] text-[#111111] text-4xl sm:text-5xl">
                            Still got a question we didn&apos;t answer?
                        </h2>
                        <p className="mt-4 font-['Manrope'] text-base md:text-lg text-[#111111]/85 max-w-2xl">
                            Easiest way to find out — pick up the phone. Real person, real answer, no phone tree.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3">
                        <a href={SITE.phoneHref} className="block flex-1" data-testid="faq-cta-call">
                            <Button className="rounded-none w-full h-14 px-6 bg-[#111111] hover:bg-[#000000] text-white font-bold tracking-wide text-base font-['Manrope']">
                                <Phone className="w-5 h-5 mr-2" strokeWidth={2.5} />
                                CALL {SITE.phoneDisplay}
                            </Button>
                        </a>
                        <a href="/#contact" className="block flex-1" data-testid="faq-cta-form">
                            <Button variant="outline" className="rounded-none w-full h-14 px-6 border-2 border-[#111111] bg-transparent text-[#111111] hover:bg-[#111111] hover:text-white font-bold tracking-wide text-base font-['Manrope']">
                                ASK BY EMAIL
                                <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                            </Button>
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
